import type { AttachmentItem } from '@ruby/shared';
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { protectedFileUrl, openFile, errorMessage } from '@/lib/api';
import { toast } from '@/lib/toast';
import { cn, formatDateTime } from '@/lib/utils';

/**
 * Attachments open inside the system (owner, 2026-11-03: "view attached files directly, without downloading them"):
 * pictures and PDFs are shown on the page — pictures with zoom and rotate, PDFs in the browser's own reader with its
 * pages, search and print; text / CSV as text. Other files (Excel, Word) say so and offer the download. Arrows or
 * ← / → move through the files, Esc closes, and the strip at the bottom shows them all.
 */

export const sizeText = (b: number) => (b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const kindOf = (a: AttachmentItem) =>
  a.contentType.startsWith('image/') && !/heic|heif/i.test(a.contentType) ? 'image' : a.contentType === 'application/pdf' || /\.pdf$/i.test(a.fileName) ? 'pdf' : /^text\/|csv/.test(a.contentType) || /\.(csv|txt)$/i.test(a.fileName) ? 'text' : 'other';

export function FileIcon({ a, className }: { a: AttachmentItem; className?: string }) {
  const k = kindOf(a);
  return k === 'image' ? <ImageIcon className={className} /> : /sheet|excel|csv/.test(a.contentType) || /\.(xlsx?|csv)$/i.test(a.fileName) ? <FileSpreadsheet className={className} /> : <FileText className={className} />;
}

/** A small picture of an image attachment (fetched once); other files show their icon. */
export function AttachmentThumb({ a, className }: { a: AttachmentItem; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (kindOf(a) !== 'image') return;
    let alive = true;
    void protectedFileUrl(`/attachments/${a.id}`).then((u) => alive && setSrc(u)).catch(() => undefined);
    return () => { alive = false; };
  }, [a]);
  const pdf = kindOf(a) === 'pdf';
  return (
    <span className={cn('relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-2 text-fg-muted ring-1 ring-border', pdf && 'bg-danger-soft text-danger', className)}>
      {src ? <img src={src} alt="" className="size-full object-cover" /> : <FileIcon a={a} className="size-[45%]" />}
      {pdf && <span className="absolute right-0.5 bottom-0.5 rounded bg-danger px-0.5 text-[7px] font-bold text-white">PDF</span>}
    </span>
  );
}

export function AttachmentViewer({ files, start, onClose }: { files: AttachmentItem[]; start: number; onClose(): void }) {
  const [i, setI] = useState(start);
  const a = files[i]!;
  const k = kindOf(a);
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [turn, setTurn] = useState(0);

  useEffect(() => {
    setUrl(null); setText(null); setFailed(null); setZoom(1); setTurn(0);
    if (k === 'other') return;
    let alive = true;
    void protectedFileUrl(`/attachments/${a.id}`)
      .then(async (u) => {
        if (!alive) return;
        if (k === 'text') setText((await (await fetch(u)).text()).slice(0, 200_000));
        else setUrl(u);
      })
      .catch((e) => alive && setFailed(errorMessage(e)));
    return () => { alive = false; };
  }, [a, k]);

  const go = (d: number) => setI((x) => (x + d + files.length) % files.length);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && files.length > 1) go(1);
      else if (e.key === 'ArrowLeft' && files.length > 1) go(-1);
      else if ((e.key === '+' || e.key === '=') && k === 'image') setZoom((z) => Math.min(4, z + 0.25));
      else if (e.key === '-' && k === 'image') setZoom((z) => Math.max(0.25, z - 0.25));
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  });

  const download = () => void openFile(`/attachments/${a.id}`, a.fileName, false, true).catch((e) => toast.error(errorMessage(e)));
  const tab = () => url && window.open(url, '_blank', 'noopener');
  const tool = 'flex size-9 items-center justify-center rounded-lg text-white/85 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-40 [&_svg]:size-[18px]';

  return createPortal(
    <div role="dialog" aria-modal aria-label={`Attachment ${a.fileName}`} className="fx-overlay fixed inset-0 z-[70] flex flex-col bg-slate-950/90 text-white backdrop-blur-sm" data-state="open">
      {/* Top bar: what it is, where it is in the set, and the tools. */}
      <header className="flex items-center gap-3 border-b border-white/10 px-4 py-2.5">
        <span className="flex size-9 items-center justify-center rounded-lg bg-white/10 [&_svg]:size-[18px]"><FileIcon a={a} /></span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold" title={a.fileName}>{a.fileName}</p>
          <p className="truncate text-[11.5px] text-white/60">{sizeText(a.sizeBytes)} · {a.uploadedBy ?? '—'} · {formatDateTime(a.uploadedAt)}{files.length > 1 ? ` · ${i + 1} of ${files.length}` : ''}</p>
        </div>
        {k === 'image' && (
          <>
            <button type="button" className={tool} onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} title="Zoom out (−)" aria-label="Zoom out"><ZoomOut /></button>
            <span className="w-12 text-center text-[12px] tabular-nums text-white/70">{Math.round(zoom * 100)}%</span>
            <button type="button" className={tool} onClick={() => setZoom((z) => Math.min(4, z + 0.25))} title="Zoom in (+)" aria-label="Zoom in"><ZoomIn /></button>
            <button type="button" className={tool} onClick={() => setTurn((t) => (t + 90) % 360)} title="Rotate" aria-label="Rotate"><RotateCw /></button>
          </>
        )}
        {(k === 'image' || k === 'pdf') && <button type="button" className={tool} onClick={tab} disabled={!url} title="Open in a new tab" aria-label="Open in a new tab"><ExternalLink /></button>}
        <button type="button" className={tool} onClick={download} title="Download" aria-label="Download"><Download /></button>
        <button type="button" className={cn(tool, 'ml-1 bg-white/10')} onClick={onClose} title="Close (Esc)" aria-label="Close"><X /></button>
      </header>

      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        {files.length > 1 && <button type="button" onClick={() => go(-1)} className="absolute left-3 z-10 flex size-11 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20" aria-label="Previous file"><ChevronLeft className="size-6" /></button>}
        {failed ? (
          <p className="max-w-md text-center text-[14px] text-white/80">Could not open the file: {failed}</p>
        ) : k === 'other' ? (
          <div className="max-w-sm text-center">
            <span className="mx-auto mb-3 flex size-16 items-center justify-center rounded-2xl bg-white/10 [&_svg]:size-8"><FileIcon a={a} /></span>
            <p className="text-[15px] font-semibold">This kind of file cannot be shown here</p>
            <p className="mt-1 text-[13px] text-white/70">{/\.(xlsx?|docx?)$/i.test(a.fileName) ? 'Excel and Word files open in their own program.' : 'It opens in its own program.'} Download it to open it.</p>
            <button type="button" onClick={download} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-[13px] font-semibold text-slate-900 hover:bg-white/90"><Download className="size-4" /> Download</button>
          </div>
        ) : k === 'text' ? (
          text === null ? <Loader2 className="size-8 animate-spin text-white/70" /> : <pre className="m-6 max-h-full w-full max-w-5xl overflow-auto rounded-xl bg-white p-5 text-[12.5px] leading-relaxed text-slate-900">{text}</pre>
        ) : !url ? (
          <Loader2 className="size-8 animate-spin text-white/70" />
        ) : k === 'pdf' ? (
          // The browser's own PDF reader: pages, zoom, search and print.
          <iframe title={a.fileName} src={`${url}#view=FitH`} className="h-full w-full max-w-6xl bg-white" />
        ) : (
          <div className="flex size-full items-center justify-center overflow-auto p-6" onWheel={(e) => { if (e.ctrlKey) { e.preventDefault(); setZoom((z) => Math.min(4, Math.max(0.25, z - Math.sign(e.deltaY) * 0.1))); } }}>
            <img src={url} alt={a.fileName} draggable={false} style={{ transform: `scale(${zoom}) rotate(${turn}deg)` }} className="max-h-full max-w-full rounded-md object-contain shadow-2xl transition-transform duration-200" onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))} />
          </div>
        )}
        {files.length > 1 && <button type="button" onClick={() => go(1)} className="absolute right-3 z-10 flex size-11 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20" aria-label="Next file"><ChevronRight className="size-6" /></button>}
      </div>

      {files.length > 1 && (
        <footer className="flex justify-center gap-2 overflow-x-auto border-t border-white/10 px-4 py-2.5">
          {files.map((f, n) => (
            <button key={f.id} type="button" onClick={() => setI(n)} title={f.fileName} className={cn('rounded-lg p-0.5 transition', n === i ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-100')}>
              <AttachmentThumb a={f} className="size-12" />
            </button>
          ))}
        </footer>
      )}
    </div>,
    document.body,
  );
}
